import React, { useEffect, useState } from 'react';

/**
 * Full-screen overlay that animates a gift emoji flying from the center
 * to the top-right (recipient zone) with sparkles and an explosion finish.
 *
 * Usage:
 *   const [fly, setFly] = useState(null);
 *   ...
 *   setFly({ emoji: gift.emoji, name: gift.name });
 *   ...
 *   {fly && <GiftFly emoji={fly.emoji} name={fly.name} onDone={() => setFly(null)} />}
 */
export default function GiftFly({ emoji = '🎁', name = '', onDone, duration = 1900 }) {
  const [phase, setPhase] = useState(0); // 0 enter, 1 fly, 2 explode

  useEffect(() => {
    const t1 = setTimeout(() => setPhase(1), 350);
    const t2 = setTimeout(() => setPhase(2), duration - 600);
    const t3 = setTimeout(() => onDone && onDone(), duration);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [duration, onDone]);

  // 16 sparkle particles in a ring
  const sparkles = Array.from({ length: 16 }).map((_, i) => {
    const angle = (i / 16) * Math.PI * 2;
    const dx = Math.cos(angle) * 140;
    const dy = Math.sin(angle) * 140;
    return { dx, dy, delay: i * 22, hue: 30 + ((i * 23) % 320) };
  });

  return (
    <div className="fixed inset-0 z-[200] pointer-events-none flex items-center justify-center">
      {/* Glow flash */}
      <div className={`absolute inset-0 transition-opacity duration-500 ${phase >= 1 ? 'opacity-70' : 'opacity-0'} ${phase === 2 ? 'opacity-0' : ''}`} style={{ background: 'radial-gradient(circle, rgba(251,191,36,0.25) 0%, rgba(244,114,182,0.18) 30%, transparent 60%)' }} />

      {/* Trailing comet ribbon */}
      {phase >= 1 && phase < 2 && (
        <div className="absolute" style={{ animation: 'gift-trail 1100ms cubic-bezier(0.55, 0, 0.65, 1) both' }}>
          <div className="w-72 h-1 rounded-full" style={{ background: 'linear-gradient(90deg, transparent, rgba(251,191,36,0.7), rgba(244,114,182,0.6), transparent)', filter: 'blur(3px)' }} />
        </div>
      )}

      {/* Main gift emoji */}
      <div
        className={`absolute text-7xl md:text-8xl select-none ${phase === 0 ? 'gift-fly-enter' : phase === 1 ? 'gift-fly-go' : 'gift-fly-burst'}`}
        style={{ filter: 'drop-shadow(0 0 20px rgba(251,191,36,0.7))' }}
      >
        {emoji}
      </div>

      {/* Sparkle particles on burst */}
      {phase === 2 && (
        <>
          {sparkles.map((s, i) => (
            <span
              key={i}
              className="absolute text-2xl"
              style={{
                color: `hsl(${s.hue}, 90%, 65%)`,
                animation: `gift-spark 700ms ease-out ${s.delay}ms forwards`,
                ['--dx']: `${s.dx}px`,
                ['--dy']: `${s.dy}px`,
              }}
            >✦</span>
          ))}
          {/* Big star burst */}
          <div className="absolute text-7xl gift-burst-star">✨</div>
        </>
      )}

      {/* Name tag */}
      {name && phase < 2 && (
        <div className="absolute bottom-1/3 px-4 py-2 rounded-full bg-black/60 backdrop-blur text-white text-sm font-display font-semibold border border-white/15">
          {name}
        </div>
      )}

      <style>{`
        @keyframes gift-fly-enter {
          0% { transform: translate(0, 60vh) scale(0.4); opacity: 0; }
          100% { transform: translate(0, 0) scale(1); opacity: 1; }
        }
        .gift-fly-enter { animation: gift-fly-enter 380ms cubic-bezier(0.2, 0.9, 0.2, 1.2) both; }

        @keyframes gift-fly-go {
          0% { transform: translate(0, 0) scale(1) rotate(0deg); }
          50% { transform: translate(15vw, -8vh) scale(1.15) rotate(180deg); }
          100% { transform: translate(35vw, -32vh) scale(0.55) rotate(360deg); }
        }
        .gift-fly-go { animation: gift-fly-go 1100ms cubic-bezier(0.55, 0, 0.55, 1) both; }

        @keyframes gift-fly-burst {
          0% { transform: translate(35vw, -32vh) scale(0.55); opacity: 1; }
          60% { transform: translate(35vw, -32vh) scale(1.6); opacity: 1; }
          100% { transform: translate(35vw, -32vh) scale(0.2); opacity: 0; }
        }
        .gift-fly-burst { animation: gift-fly-burst 700ms cubic-bezier(0.2, 0.8, 0.2, 1) both; }

        @keyframes gift-spark {
          0% { transform: translate(0, 0) scale(1); opacity: 1; }
          100% { transform: translate(var(--dx), var(--dy)) scale(0.2); opacity: 0; }
        }

        @keyframes gift-burst-star {
          0% { transform: scale(0.2); opacity: 1; }
          50% { transform: scale(2.2); opacity: 0.9; }
          100% { transform: scale(3.5); opacity: 0; }
        }
        .gift-burst-star { animation: gift-burst-star 700ms ease-out both; }

        @keyframes gift-trail {
          0% { transform: translate(-30vw, 30vh) rotate(-30deg); opacity: 0; }
          50% { opacity: 1; }
          100% { transform: translate(40vw, -35vh) rotate(-30deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
